import { AsyncQueuer } from "@tanstack/react-pacer"
import type { RegisteredRouter } from "@tanstack/react-router"
import posthog from "posthog-js"
import {
	markTapped,
	type TapSource,
	waitForNotification,
} from "@/shared/api/websocket"
import { getSessionVersion, registerSessionCleanup } from "@/shared/lib/auth"
import { openedEventOf, setTapAttribution } from "../lib/attribution"

// The push carries only the id; the payload arrives in the replay the server
// sends once the socket is up. The wait therefore has to span the connect the
// socket itself allows (connection.ts: CONNECT_TIMEOUT) rather than a shorter
// guess — a cold start routinely needs more than a few seconds. Past it the tap
// still lands on the home screen instead of being dropped.
const CONNECT_TIMEOUT_MS = 15_000
const BURST_TIMEOUT_MS = 2000

interface Tap {
	notificationId: number | null
	source: TapSource
}

/**
 * Opens what the user tapped, once: the notification's route, home when the
 * tap carried no id or its payload never arrived. A launch from the icon
 * leaves this a no-op.
 *
 * Taps queue until the router is handed over and the user is inside the
 * authenticated zone, and then open one at a time. A cold start receives the
 * tap before the app can navigate; a tap on an already running app arrives long
 * after startup handed the router over.
 */
class TapQueue {
	private router: RegisteredRouter | null = null
	private flushing = false
	private readonly taps = new AsyncQueuer<Tap>((tap) => this.open(tap), {
		started: false,
		onError: reportOpenError,
	})

	/** Taps waiting or being opened. */
	readonly store = this.taps.store

	add(tap: Tap): void {
		this.taps.addItem(tap)
	}

	/**
	 * Hands the router over once the home screen is ready: the tap that started
	 * the app opens here, every later one as it arrives.
	 */
	async attachRouter(router: RegisteredRouter): Promise<void> {
		this.router = router
		router.subscribe("onResolved", () => {
			void this.startWhenAuthenticated()
		})
		await this.startWhenAuthenticated()
	}

	reset(): void {
		this.taps.stop()
		this.taps.clear()
	}

	private async startWhenAuthenticated(): Promise<void> {
		const router = this.router
		if (router === null || this.flushing) return
		if (this.taps.store.state.isRunning) return
		if (!router.state.matches.some((m) => m.routeId === "/_authenticated"))
			return

		this.flushing = true
		try {
			const version = getSessionVersion()
			while (!this.taps.store.state.isEmpty) await this.taps.flush(1)
			if (version !== getSessionVersion()) return
			this.taps.start()
		} finally {
			this.flushing = false
		}
	}

	private async open(tap: Tap): Promise<void> {
		const router = this.router
		if (router === null) return
		const version = getSessionVersion()
		const { notificationId, source } = tap
		let notification: Awaited<ReturnType<typeof waitForNotification>> = null
		if (notificationId !== null) {
			notification = await waitForNotification(notificationId, {
				connectMs: CONNECT_TIMEOUT_MS,
				burstMs: BURST_TIMEOUT_MS,
			})
		}
		if (version !== getSessionVersion()) return
		setTapAttribution(
			source,
			notificationId,
			notification?.responseName ?? null,
		)
		posthog.capture(openedEventOf(source), {
			notification_id: notificationId,
			notification_name: notification?.responseName ?? null,
		})
		// The tap is answered on landing: `confirmOpenedNotifications` confirms the
		// notification once the router resolves its route.
		if (notification && notificationId !== null)
			markTapped(notificationId, source)
		const target = notification?.route ?? { to: "/home" as const }
		await router.navigate({ ...target }).catch(reportNavigateError)
	}
}

function reportOpenError(error: Error): void {
	posthog.captureException(error, { scope: "push.open" })
}

function reportNavigateError(error: unknown): void {
	posthog.captureException(error, { scope: "push.navigate" })
}

export const notificationTaps = new TapQueue()

registerSessionCleanup(() => notificationTaps.reset())
